import React from 'react';
import SurveyResponse from '../../util/SurveyResponse';
import useSubjectsList from '../../../../hooks/useSubjectsList';
import { useSelector } from 'react-redux';
import { selectUserPatient } from '../../../../redux/modules/userPatients/selectors';

export interface WithResponseSubjectInjectedProps {
  patient?: fhir.Patient;
}

const withResponseSubject = <P extends object>(
  WrappedComponent: React.ComponentType<P & WithResponseSubjectInjectedProps>,
) => {
  const HOC: React.FC<P & { surveyResponse: SurveyResponse }> = props => {
    const { surveyResponse } = props;
    const placeHolderData = {
      pages: [{ patientList: [] }],
      pageParams: [],
    } as { pages: { patientList: any[] }[]; pageParams: any[] };

    const patientId =
      surveyResponse.responseWithoutItem?.subject?.reference?.replace(
        'Patient/',
        '',
      );

    const currentSubject = useSelector(selectUserPatient);
    const isCurrentSubject = React.useMemo(() => {
      const isCurrentSubject = currentSubject?.id === patientId;
      if (isCurrentSubject) {
        // No need to query for the current subject
        // Bypass the hook by adding the current subject to the placeholder data
        placeHolderData.pages[0].patientList.push(currentSubject);
      }
      return isCurrentSubject;
    }, [currentSubject, patientId, placeHolderData.pages]);

    const fhirSearchPatients = React.useMemo(() => {
      // If the subject is the current user, we don't need to query for it
      // Passing an empty array will force the use of the placeholder data
      return new Set(!isCurrentSubject && patientId ? [patientId] : []);
    }, [patientId, isCurrentSubject]);

    const { data } = useSubjectsList(fhirSearchPatients, placeHolderData);
    const patient: fhir.Patient | undefined =
      data?.pages?.[0]?.patientList?.[0];

    return <WrappedComponent {...props} patient={patient} />;
  };

  return HOC;
};

export default withResponseSubject;
